import React, { useEffect, useRef, useState } from 'react';
import 'styles/main.css';

import moment from 'moment-timezone';
import update from 'immutability-helper';

import API from 'files/api.js';
import AddressLookupField from 'views/AddressLookupField.js';
import Alert from 'views/Alert.js';
import AlertStack from 'views/AlertStack.js';
import Appearance from 'styles/Appearance.js';
import Button from 'views/Button.js';
import Checkbox from 'views/Checkbox.js';
import DatePicker, { DualDatePicker } from 'views/DatePicker.js';
import DateDurationPickerField from 'views/DateDurationPickerField.js';
import FilePickerField from 'views/FilePickerField.js';
import Loader from 'views/Loader.js';
import LottieView from 'views/Lottie.js';
import PickerField from 'views/PickerField.js';
import QueryString from 'query-string';
import Request from 'files/Request.js';
import RequestAgreement from 'views/RequestAgreement.js';
import Sheet from 'views/Sheet.js';
import Template from 'classes/Template.js';
import TextField from 'views/TextField.js';
import TextView from 'views/TextView.js';
import Utils from 'files/Utils.js';

window.theme = 'light';

const App = () => {

    const container = useRef(null);
    const spacing = 30;
    const tempDate = useRef(moment().add(48, 'hours'));

    const [alerts, setAlerts] = useState([]);
    const [answers, setAnswers] = useState({});
    const [complete, setComplete] = useState(false);
    const [datePicker, setDatePicker] = useState(null);
    const [dealership, setDealership] = useState(null);
    const [embeded, setEmbeded] = useState(false);
    const [enrollmentUserID, setEnrollmentUserID] = useState(null);
    const [kiosk, setKiosk] = useState(false);
    const [loader, setLoader] = useState(null);
    const [loading, setLoading] = useState(false);
    const [nonce, setNonce] = useState(moment().unix());
    const [preview, setPreview] = useState(false);
    const [privacyAccepted, setPrivacyAccepted] = useState(false);
    const [program, setProgram] = useState(null);
    const [requestAgreement, setRequestAgreement] = useState(null);
    const [sheet, setSheet] = useState(null);
    const [size, setSize] = useState({ height: window.innerHeight, width: window.innerWidth });
    const [surveyAnswers, setSurveyAnswers] = useState({});
    const [template, setTemplate] = useState(null);
    const [theme, setTheme] = useState(window.theme);

    const utils = {
        alert: {
            show: async props => {
                try {
                    if(loader) {
                        await utils.loader.hide();
                    }
                    setAlerts(alerts => update(alerts, {
                        $push: [{
                            id: `${moment().unix()}-${Math.random()}`,
                            ...props
                        }]
                    }))
                } catch(e) {
                    console.error(e.message);
                }
            },
            showAsync: async props => {
                return new Promise(async (resolve, reject) => {
                    try {
                        if(loader) {
                            await utils.loader.hide();
                        }
                        setAlerts(alerts => update(alerts, {
                            $push: [{
                                id: `${moment().unix()}-${Math.random()}`,
                                onClose: resolve,
                                ...props
                            }]
                        }))
                    } catch(e) {
                        reject(e.message)
                    }
                })
            },
        },
        api: {
            headers: () => {
                return {
                    'Content-Type': 'application/json',
                    'X-Timezone': `TZ ${moment.tz.guess()}`,
                    'X-API': `Version ${API.version}`
                }
            }
        },
        datePicker: {
            show: props => setDatePicker({
                utils: utils,
                id: `${moment().unix()}-${Math.random()}`,
                ...props
            }),
            showDual: props => {
                utils.layer.open({
                    id: 'dual_date_picker_alert',
                    Component: DualDatePicker.bind(this, {
                        ...props,
                        utils: utils
                    })
                })
            }
        },
        loader: {
            show: async () => {
                return new Promise(resolve => {
                    setLoader(true);
                    setTimeout(resolve, 500)
                })
            },
            hide: async () => {
                return new Promise(resolve => {
                    setLoader(false);
                    setTimeout(resolve, 500)
                })
            }
        },
        sheet: {
            show: (sheet, callback) => {
                setSheet({ ...sheet, onClick: callback })
            }
        }
    }

    const onDoneClick = async props => {
        try {

            // prevent moving forward if preview is enabled
            if(preview) {
                throw new Error('This content is provided for preview purposes only. Pressing this button will not submit any information.');
            }

            // verify that all required survey answers have been provided
            let required = template.questions.filter(question => question.required === true);
            if(required.length > Object.values(surveyAnswers).length) {
                throw new Error('Please fill out all the required fields before moving on.');
            }

            // determine if a required personal question was not answered
            getPersonalFields().forEach(field => {
                if(field.required === true && !field.value) {
                    throw new Error('Please fill out all the required fields before moving on.');
                }
            });

            // determine if the survey or program is configured to request a demo
            if(template.request_demo) {

                // verify that all spouse fields have been completed
                getSpouseFields().forEach(field => {
                    if(field.required === true && !field.value) {
                        throw new Error('Please fill out all the required fields before moving on.');
                    }
                });
            }

            // verify that terms and conditions have been accepted if applicable
            if(program && program.privacy && privacyAccepted !== true) {
                throw new Error('Please review and accept the terms and conditions for this survey before moving on.');
            }

            // determine which steps to take based on the template category
            switch(template && template.category || '') {
                case 'lead_generation':
                return onSubmitLeadData(props);

                case 'recruiting':
                return onSubmitRecruitingData(props);

                default:
                return onSubmitLeadData(props);
            }

        } catch(e) {
            setLoading(false);
            if(e.code === 409 && e.response.do_not_call) {
                utils.alert.show({
                    title: 'Just a Second',
                    message: `It looks like your information is on our Do Not Call list. Would you like to remove yourself from this list and continue with your submission?`,
                    buttons: [{
                        key: 'confirm',
                        title: 'Yes',
                        style: 'default'
                    },{
                        key:' cancel',
                        title: 'Do Not Remove',
                        style: 'destructive'
                    }],
                    onClick: key => {
                        if(key === 'confirm') {
                            onDoneClick({ do_not_call_override: true });
                            return;
                        }
                    }
                });
                return;
            }
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue submitting your information. ${e.message || 'An unknown error occurred'}`
            });
        }
    }

    const onPrivacyClick = () => {
        setRequestAgreement({
            content: program.privacy,
            onAccept: () => setPrivacyAccepted(true),
            onDecline: () => setPrivacyAccepted(false)
        });
    }

    const onSetStyleSheetProperties = () => {
        setTheme(window.theme);
        document.body.className = window.theme;
        document.documentElement.style.setProperty('--divider', Appearance.colors.divider());
        document.documentElement.style.setProperty('--green', Appearance.colors.green);
        document.documentElement.style.setProperty('--grey', Appearance.colors.grey());
        document.documentElement.style.setProperty('--soft_border', Appearance.colors.softBorder());
        document.documentElement.style.setProperty('--text', Appearance.colors.text());
        document.documentElement.style.setProperty('--textfield', Appearance.colors.textField());
        document.documentElement.style.setProperty('--theme', window.theme);
        document.querySelector('meta[name="theme-color"]').setAttribute("content", Appearance.colors.background());
    }

    const onSubmitLeadData = async props => {
        try {

            // submit program answers for new lead
            setLoading('submit');
            await Request.post(utils, '/leads/', {
                dealership_id: dealership && dealership.id,
                demo_request: template.request_demo ? true : false,
                enrollment_user_id: enrollmentUserID,
                program_id: program && program.id,
                survey: surveyAnswers,
                type: 'new_from_customer',
                ...answers,
                ...props
            });

            setComplete(true);
            setLoading(false);

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue submitting your information. ${e.message || 'An unknown error occurred'}`
            });
        }
    }

    const onSubmitRecruitingData = async props => {
        try {

            // submit program answers for new lead
            setLoading('submit');
            await Request.post(utils, '/leads/', {
                dealership_id: dealership && dealership.id,
                enrollment_user_id: enrollmentUserID,
                program_id: program && program.id,
                survey: surveyAnswers,
                type: 'new_recruiting',
                ...answers,
                ...props
            });

            setComplete(true);
            setLoading(false);

        } catch(e) {
            setLoading(false);
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue submitting your information. ${e.message || 'An unknown error occurred'}`
            });
        }
    }

    const onUpdateTheme = evt => {
        window.theme = evt.matches ? 'dark' : 'light';
        onSetStyleSheetProperties();
        setNonce(moment().unix());
    }

    const onWindowSizeChange = () => {
        setSize({
            height: window.innerHeight,
            width: window.innerWidth
        });
    }

    const getBackgroundImage = () => {
        let image = getProp('background_image');
        if(!image) {
            return null;
        }
        return {
            backgroundImage: `url(${image.url})`,
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            backgroundSize: 'cover',
            opacity: 1
        }
    }

    const getButtons = () => {

        // no submission buttons are required for preview content
        if(preview === true) {
            return null;
        }

        // prepare default buttons array
        let buttons = [{
            color: 'primary',
            key: 'submit',
            label: 'Submit',
            loading: loading === 'submit',
            onClick: onDoneClick.bind(this, {})
        }];

        return (
            <div style={{
                alignItems: 'center',
                display: 'flex',
                flexDirection: 'row',
                marginBottom: 40,
                width: '100%'
            }}>
                {buttons.filter(button => {
                    return button.visible !== false;
                }).map((button, index, buttons) => {
                    return (
                        <div
                        key={index}
                        style={{
                            paddingLeft: index > 0 && buttons.length > 1 ? 4 : 0,
                            paddingRight: index === 0 && buttons.length > 1 ? 4 : 0,
                            width: buttons.length > 1 ? '50%' :'100%'
                        }}>
                            <Button
                            {...button}
                            key={index}
                            type={'large'}
                            style={{
                                ...hasBackgroundImage() && ({
                                    border: '2px solid white',
                                    boxShadow: null
                                })
                            }}/>
                        </div>
                    )
                })}
            </div>
        )
    }

    const getComponent = item => {
        switch(item.component) {
            case 'address_lookup':
            return (
                <AddressLookupField
                geocode={true}
                icon={item.icon}
                utils={utils}
                value={answers && answers.address && {
                    address: answers.address,
                    location: answers.location
                }}
                style={{
                    width: '100%'
                }}
                onChange={place => {
                    setAnswers(answers => update(answers, {
                        $set: {
                            ...answers,
                            address: place && {
                                administrative_area_level_1: place.administrative_area_level_1,
                                country: place.country,
                                locality: place.locality,
                                postal_code: place.postal_code,
                                street_address_1: place.street_address_1,
                                street_address_2: place.street_address_2
                            },
                            location: place && place.location
                        }
                    }))
                }}/>
            );

            case 'textfield':
            return (
                <TextField
                autoComplete={item.autoComplete}
                format={item.format}
                icon={item.icon}
                name={item.name}
                onChange={text => {
                    setAnswers(answers => {
                        return update(answers, {
                            [item.key]: {
                                $set: text
                            }
                        });
                    });
                }}
                style={{
                    width: '100%'
                }}
                value={answers && answers[item.key]}/>
            )
        }
    }

    const getCustomQuestions = () => {

        let { questions } = template;
        return questions && questions.length > 0 && (
            <PanelContainer
            spacing={spacing}
            hasBackgroundImage={hasBackgroundImage()}>
                {questions.sort((a,b) => {
                    return a.order_index > b.order_index;
                }).map((question, index) => {
                    return (
                        <div
                        key={index}
                        style={{
                            marginBottom: index !== questions.length - 1 ? 25 : 0
                        }}>
                            <div style={{
                                alignItems: 'center',
                                display: 'flex',
                                flexDirection: 'row',
                                marginBottom: 8,
                                width: '100%'
                            }}>
                            {question.required && !surveyAnswers[question.id] && (
                                <div style={{
                                    borderRadius: 2.5,
                                    backgroundColor: Appearance.colors.red,
                                    height: 5,
                                    marginRight: 8,
                                    overflow: 'hidden',
                                    width: 5
                                }} />
                            )}
                            <span style={{
                                ...Appearance.textStyles.subHeader(),
                                display: 'block',
                                flexGrow: 1,
                                whiteSpace: 'normal'
                            }}>{question.title}</span>
                            </div>
                            {getQuestionComponent(question)}
                        </div>
                    )
                })}
            </PanelContainer>
        )
    }

    const getDemoRequestComponents = () => {
        return template.request_demo && (
            <PanelContainer
            hasBackgroundImage={hasBackgroundImage()}
            spacing={spacing}
            title={'Request a Demonstration'}>
                <div style={{
                    alignItems: 'center',
                    display: 'flex',
                    flexDirection: 'row',
                    marginBottom: 8,
                    width: '100%'
                }}>
                    {!answers.date && (
                        <div style={{
                            backgroundColor: Appearance.colors.red,
                            borderRadius: 2.5,
                            height: 5,
                            marginRight: 8,
                            overflow: 'hidden',
                            width: 5
                        }} />
                    )}
                    <span style={{
                        ...Appearance.textStyles.subHeader(),
                        display: 'block',
                        flexGrow: 1
                    }}>{'Date and Time'}</span>
                </div>
                <DateDurationPickerField
                onChange={date => {
                    setAnswers(answers => {
                        return update(answers, {
                            date: {
                                $set: date
                            }
                        });
                    });
                }}
                selected={answers && answers.date ? moment(answers.date) : tempDate.current}
                utils={utils}/>
                <span style={{
                    ...Appearance.textStyles.subTitle(),
                    display: 'block',
                    marginTop: 8,
                    whiteSpace: 'wrap'
                }}>{'Please note that this is a request and does not book an actual demo. We recommend requesting at least 48 hours ahead for all demos.'}</span>
            </PanelContainer>
        )
    }

    const getHeaderComponents = () => {

        // declare header customizations
        let headerImage = getProp('header_image');
        let message = getProp('welcome_message');

        // determine if a header image was provided
        if(headerImage && headerImage.url) {
            return (
                <>
                <img
                src={headerImage.url}
                style={{
                    borderBottom: `1px solid ${Appearance.colors.divider()}`,
                    height: 250,
                    objectFit: 'cover',
                    overflow: 'hidden',
                    width: '100%'
                }} />
                <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    padding: 20,
                    width: '100%'
                }}>
                    <span style={{
                        color: Appearance.colors.text(),
                        fontSize: 18,
                        fontWeight: '800'
                    }}>{program ? program.name : template.title}</span>
                    <span style={{
                        ...Appearance.textStyles.title(),
                        color: Appearance.colors.subText()
                    }}>{dealership ? `Sponsored by ${dealership.name}` : 'Your Dealership Name Here'}</span>
                    <span style={{
                        ...Appearance.textStyles.subTitle(),
                        color: Appearance.colors.text(),
                        marginTop: 20,
                        whiteSpace: 'wrap'
                    }}>{message}</span>
                </div>
                </>
            )
        }
           
        return (
            <div style={{
                alignItems: 'center',
                borderRadius: 10,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                overflow: 'hidden',
                padding: 20,
                textAlign: 'center',
                width: '100%'
            }}>
                <LottieView
                autoPlay={true}
                loop={true}
                source={require('files/lottie/thank-you-blue.json')}
                style={{
                    height: 65,
                    marginBottom: 8,
                    width: 65
                }} />
                <span style={{
                    ...Appearance.textStyles.header(),
                    color: Appearance.colors.text()
                }}>{program ? program.name : template.title}</span>
                <span style={{
                    ...Appearance.textStyles.title(),
                    color: Appearance.colors.subText()
                }}>{dealership ? `Sponsored by ${dealership.name}` : 'Your Dealership Name Here'}</span>
                <span style={{
                    ...Appearance.textStyles.subTitle(),
                    color: Appearance.colors.text(),
                    marginTop: 20,
                    whiteSpace: 'wrap'
                }}>{message}</span>
            </div>
        )       
    }

    const getPanelStyles = () => {
        return {
            ...Appearance.styles.unstyledPanel(),
            border: `1px solid ${Appearance.colors.softBorder()}`,
            ...hasBackgroundImage() && ({
                boxShadow: null
            })
        }
    }

    const getPersonalFields = () => {
        return [{
            autoComplete: 'on',
            component: 'textfield',
            icon: 'user',
            key: 'first_name',
            name: 'given_name',
            title: 'First Name',
            value: answers.first_name
        },{
            autoComplete: 'on',
            component: 'textfield',
            icon: 'user',
            key: 'last_name',
            name: 'family_name',
            title: 'Last Name',
            value: answers.last_name
        },{
            autoComplete: 'on',
            component: 'textfield',
            format: 'phone_number',
            icon: 'phone',
            key: 'phone_number',
            name: 'tel',
            title: 'Phone Number',
            value: answers.phone_number
        },{
            autoComplete: 'on',
            component: 'textfield',
            icon: 'email',
            key: 'email_address',
            name: 'email',
            required: template && template.category === 'recruiting' ? true : false,
            title: 'Email Address',
            value: answers.email_address
        },{
            autoComplete: 'off',
            component: 'address_lookup',
            icon: 'house',
            key: 'address',
            required: template && template.category === 'recruiting' ? true : false,
            title: 'Home Address',
            value: answers.address
        }];
    }

    const getPreviewBanner = () => {
        if(!preview) {
            return null;
        }
        return (
            <div style={{
                background: Appearance.colors.softGradient(Appearance.colors.red),
                border: `2px solid ${Appearance.colors.red}`,
                borderRadius: 10,
                marginBottom: 20,
                overflow: 'hidden',
                padding: '8px 12px 8px 12px',
                textAlign: 'center',
                width: '100%'
            }}>
                <span style={{
                    color: 'white',
                    fontSize: 16,
                    fontWeight: 700
                }}>{`This content is provided for preview purposes only`}</span>
            </div>
        )
    }

    const getPrimaryQuestions = () => {
        return (
            <PanelContainer
            hasBackgroundImage={hasBackgroundImage()}
            spacing={spacing}
            title={'About You'}>
                {getPersonalFields().map((item, index, items) => {
                    return (
                        <div
                        key={index}
                        style={{
                            marginBottom: index !== items.length - 1 ? 25 : 0
                        }}>
                            <div style={{
                                alignItems: 'center',
                                display: 'flex',
                                flexDirection: 'row',
                                marginBottom: 8,
                                width: '100%'
                            }}>
                                {!answers[item.key] && item.required !== false && (
                                    <div style={{
                                        backgroundColor: Appearance.colors.red,
                                        borderRadius: 2.5,
                                        height: 5,
                                        marginRight: 8,
                                        overflow: 'hidden',
                                        width: 5
                                    }} />
                                )}
                                <span style={{
                                    ...Appearance.textStyles.subHeader(),
                                    display: 'block',
                                    flexGrow: 1
                                }}>{item.title}</span>
                            </div>
                            {getComponent(item)}
                        </div>
                    )
                })}
            </PanelContainer>
        )
    }

    const getPrivacyComponent = () => {
        return program && program.privacy && (
            <PanelContainer
            hasBackgroundImage={hasBackgroundImage()}
            spacing={spacing}>
                <div style={{
                    alignItems: 'center',
                    display: 'flex',
                    flexDirection: 'row',
                    width: '100%'
                }}>
                    <Checkbox
                    checked={privacyAccepted}
                    enabled={true}
                    onChange={setPrivacyAccepted} />
                    <div style={{
                        paddingLeft: 12
                    }}>
                        <span style={{
                            ...Appearance.textStyles.title(),
                            whiteSpace: 'normal'
                        }}>
                            {'I have read and agree to the '}
                            <span
                            className={'text-button'}
                            onClick={onPrivacyClick}
                            style={{
                                color: Appearance.colors.primary(),
                                fontWeight: 700
                            }}>{'terms and conditions'}</span>
                            {' for this program'}
                        </span>
                    </div>

                </div>
            </PanelContainer>
        )
    }

    const getProp = key => {
        let { props } = template || {};
        if(!props || !props[key]) {
            return ''
        }
        return props[key];
    }

    const getQuestionComponent = question => {

        let { props } = question;
        switch(question.type) {
            case Template.Question.type.list:
            return (
                <select
                className={`custom-select ${window.theme}`}
                defaultValue={'Choose an option from the list'}
                onChange={e => {
                    let id = Utils.attributeForKey.select(e, 'id');
                    setSurveyAnswers(answers => {
                        return update(answers, {
                            [question.id]: {
                                $set: {
                                    type: question.type,
                                    value: isNaN(id) ? id : parseInt(id)
                                }
                            }
                        });
                    })
                }}
                style={{
                    fontSize: Appearance.setTextFieldFontSize(12),
                    width: '100%',
                    ...Utils.isMobile() && {
                        height: 40
                    }
                }}>
                    <option disabled={true}>{'Choose an option from the list'}</option>
                    {props.items && props.items.map((item, index) => {
                        return (
                            <option key={index} id={item.id}>{item.title}</option>
                        )
                    })}
                </select>
            )

            case Template.Question.type.checkboxes:
            return (
                <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    width: '100%'
                }}>
                    {props.items && props.items.map((item, index) => {
                        return (
                            <div
                            key={index}
                            style={{
                                alignItems: 'center',
                                display: 'flex',
                                flexDirection: 'row',
                                marginBottom: 8,
                                width: '100%'
                            }}>
                                <Checkbox
                                enabled={true}
                                onChange={enabled => {
                                    setSurveyAnswers(answers => {

                                        // prepare list of values
                                        let values = {
                                            ...answers[question.id] && answers[question.id].values,
                                            [item.id]: enabled
                                        };

                                        // reset question if no enabled values are set
                                        let match = Object.values(values).find(answer => answer === true);
                                        if(!match) {
                                            return update(answers, {$unset: question.id});
                                        }

                                        // update answer entry with selections
                                        return update(answers, {
                                            [question.id]: {
                                                $set: {
                                                    type: question.type,
                                                    values: values
                                                }
                                            }
                                        });
                                    })
                                }} />
                                <span style={{
                                    ...Appearance.textStyles.title(),
                                    marginLeft: 8
                                }}>{item.title}</span>
                            </div>
                        )
                    })}
                </div>
            )

            case Template.Question.type.picker:
            let items = props.items && props.items.map(item => {
                return {
                    key: item.id,
                    title: item.title
                }
            });
            return (
                <PickerField
                autoOpen={true}
                disableScroll={true}
                items={items}
                placeholder={'Choose an option from below...'}
                onChange={item => {
                    setSurveyAnswers(answers => {
                        return update(answers, {
                            [question.id]: {
                                $set: {
                                    type: question.type,
                                    value: item && item.key
                                }
                            }
                        });
                    })
                }}
                value={surveyAnswers[question.id] && items.find(item => item.key === surveyAnswers[question.id])}/>
            )

            case Template.Question.type.textfield:
            return (
                <TextField
                placeholder={props.placeholder}
                onChange={text => {
                    setSurveyAnswers(answers => {
                        return update(answers, {
                            [question.id]: {
                                $set: {
                                    type: question.type,
                                    value: text
                                }
                            }
                        });
                    });
                }}/>
            )

            case Template.Question.type.textview:
            return (
                <TextView
                placeholder={props.placeholder}
                onChange={text => {
                    setSurveyAnswers(answers => {
                        return update(answers, {
                            [question.id]: {
                                $set: {
                                    type: question.type,
                                    value: text
                                }
                            }
                        });
                    });
                }}/>
            )

            case Template.Question.type.file_picker:
            return (
                <FilePickerField
                onChange={result => {
                    setSurveyAnswers(answers => {
                        return update(answers, {
                            [question.id]: {
                                $set: {
                                    type: question.type,
                                    value: result
                                }
                            }
                        });
                    });
                }}/>
            )
        }
    }

    const getSpouseFields = () => {
        return [{
            autoComplete: 'off',
            component: 'textfield',
            icon: 'user',
            key: 'spouse_first_name',
            required: false,
            title: 'First Name',
            value: answers.spouse_first_name
        },{
            autoComplete: 'off',
            component: 'textfield',
            icon: 'user',
            key: 'spouse_last_name',
            required: false,
            title: 'Last Name',
            value: answers.spouse_last_name
        },{
            autoComplete: 'off',
            format: 'phone_number',
            icon: 'phone',
            key: 'spouse_phone_number',
            required: false,
            title: 'Phone Number',
            value: answers.spouse_phone_number
        },{
            autoComplete: 'off',
            component: 'textfield',
            icon: 'email',
            key: 'spouse_email_address',
            required: false,
            title: 'Email Address',
            value: answers.spouse_email_address
        }];
    }

    const getSpouseQuestions = () => {

        // no spouse questions are required if the template category is set to recruiting
        if(template && template.category === 'recruiting') {
            return null;
        }
        
        return (
            <PanelContainer
            hasBackgroundImage={hasBackgroundImage()}
            spacing={spacing}
            title={'About Your Spouse'}>
                {getSpouseFields().map((item, index, items) => {
                    return (
                        <div
                        key={index}
                        style={{
                            marginBottom: index !== items.length - 1 ? 25 : 0
                        }}>
                            <div style={{
                                alignItems: 'center',
                                display: 'flex',
                                flexDirection: 'row',
                                marginBottom: 8,
                                width: '100%'
                            }}>
                                {!answers[item.key] && item.required !== false && (
                                    <div style={{
                                        backgroundColor: Appearance.colors.red,
                                        borderRadius: 2.5,
                                        height: 5,
                                        marginRight: 8,
                                        overflow: 'hidden',
                                        width: 5
                                    }} />
                                )}
                                <span style={{
                                    ...Appearance.textStyles.subHeader(),
                                    display: 'block',
                                    flexGrow: 1
                                }}>{item.title}</span>
                            </div>
                            <TextField
                            icon={item.icon}
                            format={item.format}
                            onChange={text => {
                                setAnswers(answers => {
                                    return update(answers, {
                                        [item.key]: {
                                            $set: text
                                        }
                                    });
                                });
                            }}/>
                        </div>
                    )
                })}
            </PanelContainer>
        )
    }

    const getTemplateContent = () => {

        // no additional logic is required if a template is not available
        if(!template) {
            return null;
        }

        // determine if the survey or program has been marked as completed
        if(complete) {
            return (
                <div style={{
                    alignItems: 'center',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    height: '100%',
                    width: '100%'
                }}>
                    <div style={{
                        ...getPanelStyles(),
                        alignItems: 'center',
                        display: 'flex',
                        flexDirection: 'column',
                        padding: 20,
                        textAlign: 'center'

                    }}>
                        <LottieView
                        autoPlay={true}
                        loop={true}
                        source={require('files/lottie/thank-you-blue.json')}
                        style={{
                            height: 65,
                            marginBottom: 8,
                            width: 65
                        }} />
                        <span style={{
                            ...Appearance.textStyles.title()
                        }}>{'Questionnaire Complete'}</span>
                        <span style={{
                            ...Appearance.textStyles.subTitle()
                        }}>{'Please close your browser to end the session'}</span>
                    </div>
                </div>
            )
        }

        // render questions and custom styling for survey or program
        return (
            <div 
            ref={container}
            style={{
                display: 'flex',
                flexDirection: 'column',
                width: '100%'
            }}>
                <div style={{
                    ...getPanelStyles(),
                    alignItems: 'center',
                    display: 'flex',
                    flexDirection: 'column',
                    marginBottom: spacing,
                    textAlign: 'center'
                }}>
                    {getHeaderComponents()}
                </div>

                {getCustomQuestions()}
                {getPrimaryQuestions()}
                {getSpouseQuestions()}
                {getDemoRequestComponents()}
                {getPrivacyComponent()}
                {getButtons()}
            </div>
        )
    }

    const hasBackgroundImage = () => {
        return getProp('background_image') ? true : false;
    }

    const fetchTemplate = async () => {
        try {
            let { dealership_id, enrollment_user_id, kiosk, preview, template_id } = QueryString.parse(window.location.search);
            if(!template_id) {
                utils.alert.show({
                    title: 'Oops!',
                    message: 'It looks like this link is no longer active or is missing required information'
                });
                return;
            }

            let { dealership, template } = await Request.get(utils, '/dealerships/', {
                dealership_id: dealership_id,
                enrollment_user_id: enrollment_user_id,
                id: template_id,
                include_dealership: true,
                type: 'template_details'
            });

            setDealership(dealership);
            setEnrollmentUserID(enrollment_user_id);
            setKiosk(parseInt(kiosk));
            setPreview(parseInt(preview));
            setTemplate(Template.create(template));

        } catch(e) {
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue setting up the information for this template. ${e.message || 'An unknown error occurred'}`
            });
        }
    }

    const fetchProgram = async () => {
        try {

            let { dealership_id, enrollment_user_id, id, kiosk, preview } = QueryString.parse(window.location.search);

            // prevent moving forward if no program id was provided
            if(!id) {
                utils.alert.show({
                    title: 'Oops!',
                    message: 'It looks like this link is no longer active or is missing required information'
                });
                return;
            }

            // fetch details for program from server
            let { dealership, program } = await Request.get(utils, '/programs/', {
                dealership_id: dealership_id,
                enrollment_user_id: enrollment_user_id,
                id: id,
                include_dealership: true,
                preview: preview,
                type: 'details'
            });

            // prevent moving forward if the program is no longer active
            if(!preview && program.active === false) {
                utils.alert.show({
                    title: 'Pardon the Interruption',
                    message: 'This survey is no longer available'
                });
                return;
            }

            // prevent moving forward if the program has expired
            if(!preview && program.end_date && moment(program.end_date) < moment()) {
                utils.alert.show({
                    title: 'Pardon the Interruption',
                    message: 'This survey has expired and is no longer available'
                });
                return;
            }

            // update local state with progream details and preferences
            setDealership(dealership);
            setEnrollmentUserID(enrollment_user_id);
            setKiosk(parseInt(kiosk));
            setPreview(parseInt(preview));
            setProgram(program);
            setTemplate(Template.create(program.template));

        } catch(e) {
            utils.alert.show({
                title: 'Oops!',
                message: `There was an issue setting up the information for this survey. ${e.message || 'An unknown error occurred'}`
            });
        }
    }

    const setupTemplateDetails = () => {
        try {
            // set page title from either template title or program title
            document.title = program ? program.name : template.title;
        } catch(e) {
            console.error(e.message);
        }
    }

    useEffect(() => {
        if(template) {
            setupTemplateDetails();
        }
    }, [template]);

    useEffect(() => {
        if(container.current) {
            console.log({ height: container.current.clientHeight })
            window.parent.postMessage({ height: container.current.clientHeight });
        }
    }, [container.current, template]);

    useEffect(() => {

        // fetch template or program depending on supplied parameters
        let { embeded, id, template_id } = QueryString.parse(window.location.search);
        if(id) {
            fetchProgram();
        } else if(template_id) {
            fetchTemplate();
        }

        // set embeded flag for iframe use-cases
        setEmbeded(parseInt(embeded) === 1);

        // setup window listeners
        window.addEventListener('resize', onWindowSizeChange);
        window.addEventListener('beforeunload', (e) => {
            (e || window.event).returnValue = null;
            return null;
        });

        // css variables
        document.documentElement.style.setProperty('--primary', Appearance.colors.primary());
        document.documentElement.style.setProperty('--text', Appearance.colors.text());
        document.documentElement.style.setProperty('--textfield', Appearance.colors.textField());
        document.documentElement.style.setProperty('--soft_border', Appearance.colors.softBorder());

        // theme and theme listeners
        window.theme = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
        document.documentElement.style.setProperty('--theme', window.theme);
        window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', onUpdateTheme);
        onSetStyleSheetProperties();

    }, []);

    return (
        <div
        className={`root-container ${getProp('pattern') || ''} ${theme}`}
        nonce={nonce}
        style={{
            alignItems: 'center',
            display: 'flex',
            flexDirection: 'column',
            padding: Utils.isMobile() ? 0 : 20,
            width: '100%',
            ...embeded === false && {
                height: size.height,
                overflowY: 'scroll'
            }
        }}>
            <div style={{
                bottom: 0,
                left: 0,
                position: 'fixed',
                right: 0,
                top: 0,
                zIndex: 1,
                ...getBackgroundImage()
            }} />
            <div 
            style={{
                maxWidth: 600,
                padding: Utils.isMobile() ? 15 : 20,
                position: 'relative',
                width: '100%',
                zIndex: 2,
                ...embeded === false && {
                    height: 'calc(100vh - 40px)'
                }
            }}>
                {getPreviewBanner()}
                {getTemplateContent()}
            </div>

            <Loader animate={loader}/>
            <AlertStack>
                {alerts.map((alert, index) => (
                    <Alert {...alert}
                    index={(alerts.length - 1) - index}
                    key={index}
                    onClose={id => {
                        if(typeof(alert.onClose) === 'function') {
                            alert.onClose();
                        }
                        setAlerts(alerts => {
                            return alerts.filter(alert => {
                                return id !== alert.id;
                            });
                        })
                    }}
                    utils={utils} />
                ))}
            </AlertStack>

            {datePicker && (
                <DatePicker
                {...datePicker}
                onClose={() => {
                    setDatePicker(null);
                    if(typeof(datePicker.onClose) === 'function') {
                        datePicker.onClose();
                    }
                }} />
            )}

            {sheet && (
                <Sheet
                {...sheet}
                onClose={setSheet.bind(this, null)}/>
            )}

            {requestAgreement && (
                <RequestAgreement
                utils={utils}
                {...requestAgreement}
                onClose={() => {
                    setRequestAgreement(null);
                    if(typeof(requestAgreement.onClose) === 'function') {
                        requestAgreement.onClose();
                    }
                }}/>
            )}
        </div>
    )
}
export default App;

const PanelContainer = ({ children, hasBackgroundImage, spacing, title }) => {

    // place title inside of panel if background image is present
    // we can't guarantee the text will be readable if placed over the background image
    if(hasBackgroundImage) {
        return (
            <div style={{
                ...Appearance.styles.unstyledPanel(),
                boxShadow: null,
                marginBottom: spacing,
                width: '100%'
            }}>
                <div style={{
                    backgroundColor: Appearance.colors.divider(),
                    borderBottom: `1px solid ${Appearance.colors.divider()}`,
                    borderTopLeftRadius: 9,
                    borderTopRightRadius: 9,
                    padding: '6px 12px 6px 12px'
                }}>
                    <span style={{
                        ...Appearance.textStyles.subHeader(),
                        display: 'block'
                    }}>{title}</span>
                </div>
                <div style={{
                    padding: 20
                }}>
                    {children}
                </div>
            </div>
        )
    }

    return (
        <div style={{
            ...Appearance.styles.unstyledPanel(),
            border: `1px solid ${Appearance.colors.softBorder()}`,
            marginBottom: spacing,
            width: '100%'
        }}>
            {typeof(title) === 'string' && (
                <div style={{
                    backgroundColor: Appearance.colors.divider(),
                    padding: '8px 12px 8px 12px',
                    width: '100%'
                }}>
                    <span style={{
                        ...Appearance.textStyles.subHeader()
                    }}>{title}</span>
                </div>
            )}
            <div style={{
                padding: 20,
                width: '100%'
            }}>
                {children}
            </div>
        </div>
    )
}
